'use client';
import React, { useState, useEffect } from 'react';
import { Lock, CheckCircle, AlertTriangle, Tag, X, ArrowRight, Clock, Palette, Mail, FileText, Crown, Receipt, CreditCard, Inbox, Download } from 'lucide-react';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import type { StripeElementsOptions } from '@stripe/stripe-js';
import { getStripe } from '@/lib/stripe/client';
import type { OrderState } from './OrderWizardContent';
import { sendPaymentConfirmationEmail, scheduleArtworkReminders, sendAdminNotificationEmail, sendOrderConfirmationEmail, sendPaymentReceiptEmail } from '@/lib/email/emailService';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { PLACEMENT_PRICES, PLACEMENT_NAMES } from '@/lib/pricingConfig';
import { usePlacementCatalog } from '@/hooks/usePlacementCatalog';
import { getUserMembership, calculateAdDiscount, type UserMembership } from '@/lib/membership';

interface Props {
  order: OrderState;
  updateOrder: (updates: Partial<OrderState>) => void;
  onComplete: () => void;
}

const placementPrices = PLACEMENT_PRICES;

const placementNames = PLACEMENT_NAMES;

// ─── Coupon state types ───────────────────────────────────────────────────────
type CouponType = 'FREE_PLACEMENT' | 'DISCOUNT' | 'EXCHANGE';
type DiscountKind = 'percent' | 'fixed';

interface AppliedCoupon {
  id: string;
  code: string;
  coupon_type: CouponType;
  discount_kind: DiscountKind | null;
  discount_value: number | null;
  account_restricted?: boolean;
}

// ─── Coupon Apply Widget ──────────────────────────────────────────────────────
interface CouponWidgetProps {
  userId: string;
  baseTotal: number;
  onCouponApplied: (coupon: AppliedCoupon | null, finalTotal: number) => void;
}

function CouponWidget({ userId, baseTotal, onCouponApplied }: CouponWidgetProps) {
  const [code, setCode] = useState('');
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState<AppliedCoupon | null>(null);
  const [error, setError] = useState('');

  const computeTotal = (coupon: AppliedCoupon): number => {
    if (coupon.coupon_type === 'FREE_PLACEMENT') return 0;
    if (coupon.coupon_type === 'EXCHANGE') return 0;
    if (coupon.coupon_type === 'DISCOUNT') {
      if (coupon.discount_kind === 'percent') {
        return Math.max(0, Math.round(baseTotal * (1 - (coupon.discount_value || 0) / 100)));
      }
      return Math.max(0, baseTotal - (coupon.discount_value || 0));
    }
    return baseTotal;
  };

  const applyCoupon = async () => {
    if (!code.trim()) return;
    if (!userId) { setError('You must be logged in to apply a coupon.'); return; }
    setApplying(true);
    setError('');
    const supabase = createClient();

    // Use server-side validation RPC — prevents client-side bypass
    const { data: result, error: rpcError } = await supabase
      .rpc('validate_coupon' as any, { p_code: code.trim().toUpperCase(), p_user_id: userId });

    if (rpcError || !result) {
      setError('Failed to validate coupon. Please try again.');
      setApplying(false);
      return;
    }

    const res = result as { valid: boolean; error?: string; id?: string; code?: string; coupon_type?: string; discount_kind?: string; discount_value?: number; account_restricted?: boolean };

    if (!res.valid) {
      setError(res.error || 'Invalid coupon.');
      setApplying(false);
      return;
    }

    const coupon: AppliedCoupon = {
      id: res.id!,
      code: res.code!,
      coupon_type: res.coupon_type as CouponType,
      discount_kind: (res.discount_kind as DiscountKind) || null,
      discount_value: res.discount_value ?? null,
      account_restricted: res.account_restricted,
    };
    const finalTotal = computeTotal(coupon);
    setApplied(coupon);
    onCouponApplied(coupon, finalTotal);
    setApplying(false);
  };

  const removeCoupon = () => {
    setApplied(null);
    setCode('');
    setError('');
    onCouponApplied(null, baseTotal);
  };

  const getCouponDescription = (coupon: AppliedCoupon): string => {
    if (coupon.coupon_type === 'FREE_PLACEMENT') return 'Free placement applied — total set to $0';
    if (coupon.coupon_type === 'EXCHANGE') return 'Barter deal — order will be marked as pending barter approval';
    if (coupon.coupon_type === 'DISCOUNT') {
      if (coupon.discount_kind === 'percent') {
        if (coupon.discount_value === 100) {
          return '3rd month free — buy 2 months, get the 3rd at no charge';
        }
        return `${coupon.discount_value}% discount applied`;
      }
      return `$${coupon.discount_value} discount applied`;
    }
    return '';
  };

  if (applied) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-3 p-3 rounded-xl bg-green-500/10 border border-green-500/25">
          <Tag size={14} className="text-green-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-green-400 font-mono">{applied.code}</p>
            <p className="text-xs text-slate-400">{getCouponDescription(applied)}</p>
          </div>
          <button onClick={removeCoupon} className="text-slate-400 hover:text-white shrink-0">
            <X size={16} />
          </button>
        </div>
        {applied.coupon_type === 'DISCOUNT' && applied.discount_value === 100 && (
          <div className="flex items-start gap-2 p-3 rounded-xl bg-brand-500/8 border border-brand-500/25">
            <Tag size={12} className="text-brand-400 shrink-0 mt-0.5" />
            <p className="text-xs text-brand-300 leading-relaxed">
              <strong>How it works:</strong> You pay for 2 months now. The 3rd month is completely free — no charge on your 3rd billing cycle.
            </p>
          </div>
        )}
        {applied.account_restricted && (
          <p className="text-xs text-brand-400 flex items-center gap-1.5 px-1">
            <AlertTriangle size={11} />
            This coupon is only valid for your account.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          type="text"
          value={code}
          onChange={e => { setCode(e.target.value.toUpperCase()); setError(''); }}
          placeholder="Enter coupon code"
          className="flex-1 px-3 py-2.5 bg-navy-700 border border-white/12 rounded-xl text-white text-sm placeholder-slate-500 focus:outline-none focus:border-brand-500/50 font-mono"
          onKeyDown={e => e.key === 'Enter' && applyCoupon()}
        />
        <button
          type="button"
          onClick={applyCoupon}
          disabled={applying || !code.trim()}
          className="px-4 py-2.5 rounded-xl border border-brand-500/40 text-brand-400 text-sm font-semibold hover:bg-brand-500/10 transition-colors disabled:opacity-50"
        >
          {applying ? '...' : 'Apply'}
        </button>
      </div>
      {error && (
        <p className="text-xs text-red-400 flex items-center gap-1.5">
          <AlertTriangle size={12} />
          {error}
        </p>
      )}
    </div>
  );
}

// ─── Inner payment form (inside Elements provider) ───────────────────────────
interface PaymentFormProps {
  order: OrderState;
  updateOrder: (updates: Partial<OrderState>) => void;
  onComplete: () => void;
  clientSecret: string;
  orderId: string;
  orderNumber: string;
  paymentIntentId: string;
  verifiedTotal: number;
  artworkDeadline: string;
  deliveryDate: string;
}

function PaymentForm({
  order,
  updateOrder,
  onComplete,
  orderId,
  orderNumber,
  paymentIntentId,
  verifiedTotal,
  artworkDeadline,
  deliveryDate,
}: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const router = useRouter();
  const { user } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);

    // Confirm payment with Stripe
    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/order-success`,
      },
      redirect: 'if_required',
    });

    if (stripeError) {
      toast.error(stripeError.message || 'Payment failed. Please try again.');
      setProcessing(false);
      return;
    }

    if (paymentIntent?.status !== 'succeeded') {
      toast.error('Payment was not completed. Please try again.');
      setProcessing(false);
      return;
    }

    // ── Upload artwork file to Supabase storage (if provided) ──────────────
    if (order.artworkFile && orderId) {
      try {
        const supabase = createClient();
        const userId = user?.id ?? 'anon';
        const ext = order.artworkFile.name.split('.').pop() || 'bin';
        const filePath = `${userId}/${orderId}.${ext}`;

        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('artwork-uploads')
          .upload(filePath, order.artworkFile, {
            contentType: order.artworkFile.type,
            upsert: true,
          });

        if (uploadError) {
          console.error('[StepG] Artwork upload error:', uploadError.message);
          // Non-fatal — order is paid, just log the error
        } else {
          // Get public URL (bucket is private, so use signed URL approach via update)
          const { data: urlData } = supabase.storage
            .from('artwork-uploads')
            .getPublicUrl(filePath);

          const artworkUrl = urlData?.publicUrl || filePath;

          // Update order with artwork URL and status
          await supabase
            .from('orders')
            .update({
              artwork_review_url: artworkUrl,
              artwork_status: 'uploaded',
            })
            .eq('id', orderId);

          // Also create/update artwork_versions record
          await supabase.from('artwork_versions').insert({
            order_id: orderId,
            version_number: 1,
            file_url: artworkUrl,
            file_name: order.artworkFile.name,
            file_size_bytes: order.artworkFile.size,
            submitted_by: user?.id ?? null,
            status: 'pending_review',
          }).catch((err: Error) => console.error('[StepG] artwork_versions insert error:', err.message));

          // Trigger artwork_received email notification
          const baseUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://zipadbook.com';
          fetch(`${baseUrl}/api/orders/send-status-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ orderId, eventType: 'artwork_received' }),
          }).catch((err: Error) => console.error('[StepG] artwork_received email error:', err.message));

          console.log('[StepG] Artwork uploaded successfully:', uploadData?.path);
        }
      } catch (uploadErr) {
        console.error('[StepG] Artwork upload exception:', uploadErr);
        // Non-fatal — continue with order success flow
      }
    }

    setProcessing(false);
    setSuccess(true);
    updateOrder({ paymentComplete: true });
    onComplete();

    // Send emails (non-blocking)
    const customerEmail = user?.email || '';
    const customerName = user?.user_metadata?.full_name || '';
    if (customerEmail) {
      sendPaymentConfirmationEmail({
        customerEmail,
        customerName,
        orderId,
        orderNumber,
        placement: order.placement,
        regions: order.regions,
        total: verifiedTotal,
        term: order.term,
        artworkDeadline,
        deliveryDate,
        issueLabel: order.issueLabel,
        issuePrintDate: order.issuePrintDate,
      } as any).catch(console.error);

      // Send order confirmation email
      sendOrderConfirmationEmail({
        customerEmail,
        customerName,
        orderId,
        orderNumber,
        placement: order.placement,
        regions: order.regions,
        total: verifiedTotal,
        term: order.term,
        artworkDeadline,
        deliveryDate,
      }).catch(console.error);

      // Send payment receipt email
      sendPaymentReceiptEmail({
        customerEmail,
        customerName,
        orderId: orderNumber || orderId,
        amount: `$${verifiedTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
      }).catch(console.error);

      scheduleArtworkReminders({
        customerEmail,
        customerName,
        orderId,
        placement: order.placement,
        regions: order.regions,
        artworkDeadline,
      }).catch(console.error);

      sendAdminNotificationEmail({
        customerEmail,
        customerName,
        orderId,
        orderNumber,
        placement: order.placement,
        regions: order.regions,
        total: verifiedTotal,
        term: order.term,
        artworkDeadline,
        deliveryDate,
      }).catch(console.error);
    }
  };

  const handleGoToOrderSuccess = () => {
    const regionsStr = order.regions.join(',');
    const params = new URLSearchParams({
      orderId: orderNumber,
      placement: order.placement,
      regions: regionsStr,
      total: String(verifiedTotal),
      term: order.term,
      artworkDeadline,
      deliveryDate,
    });
    if (order.issueLabel) params.set('issueLabel', order.issueLabel);
    if (order.issuePrintDate) params.set('issuePrintDate', order.issuePrintDate);
    router.push(`/order-success?${params.toString()}`);
  };

  if (success) {
    const isDesignService = order.artworkType === 'design-service' || order.addDesign;
    const needsArtwork = !isDesignService && !order.artworkFile;
    const bookingRef = orderNumber || orderId;

    const handleDownloadInvoice = () => {
      const invoiceDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      const placementDisplay = order.placement ? order.placement.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) : '—';
      const regionsDisplay = order.regions.join(', ');
      const totalDisplay = `$${verifiedTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      const termDisplay = order.term === 'three' ? '3-Issue Subscription' : '1 Issue';

      const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Invoice - ${bookingRef}</title>
  <style>
    body { font-family: Arial, sans-serif; color: #111; margin: 0; padding: 40px; background: #fff; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px; border-bottom: 2px solid #111; padding-bottom: 20px; }
    .brand { font-size: 22px; font-weight: 800; letter-spacing: -0.5px; }
    .brand span { color: #6366f1; }
    .invoice-meta { text-align: right; font-size: 13px; color: #555; }
    .invoice-meta h2 { font-size: 20px; font-weight: 700; color: #111; margin: 0 0 4px; }
    .section-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: #888; margin: 24px 0 10px; }
    .row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f0f0f0; font-size: 14px; }
    .row:last-child { border-bottom: none; }
    .row .label { color: #555; }
    .row .value { font-weight: 600; color: #111; }
    .total-row { display: flex; justify-content: space-between; padding: 14px 0 0; font-size: 18px; font-weight: 800; border-top: 2px solid #111; margin-top: 8px; }
    .total-row .value { color: #6366f1; }
    .footer { margin-top: 40px; font-size: 11px; color: #aaa; text-align: center; border-top: 1px solid #eee; padding-top: 16px; }
    .status-badge { display: inline-block; background: #dcfce7; color: #166534; font-size: 11px; font-weight: 700; padding: 3px 10px; border-radius: 20px; margin-left: 8px; }
    @media print { body { padding: 20px; } }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="brand">Zip<span>Ad</span>Book</div>
      <div style="font-size:12px;color:#888;margin-top:4px;">zipadbook.com</div>
    </div>
    <div class="invoice-meta">
      <h2>INVOICE <span class="status-badge">PAID</span></h2>
      <div>Order #: <strong>${bookingRef}</strong></div>
      <div>Date: ${invoiceDate}</div>
      ${paymentIntentId ? `<div style="font-size:11px;color:#aaa;margin-top:2px;">Txn: ${paymentIntentId}</div>` : ''}
    </div>
  </div>

  <div class="section-title">Booking Details</div>
  ${order.issueLabel ? `<div class="row"><span class="label">Issue</span><span class="value">${order.issueLabel}</span></div>` : ''}
  <div class="row"><span class="label">Placement</span><span class="value">${placementDisplay}</span></div>
  <div class="row"><span class="label">Regions</span><span class="value">${regionsDisplay}</span></div>
  <div class="row"><span class="label">Term</span><span class="value">${termDisplay}</span></div>
  ${artworkDeadline && artworkDeadline !== 'TBD' ? `<div class="row"><span class="label">Artwork Deadline</span><span class="value">${artworkDeadline}</span></div>` : ''}
  ${deliveryDate && deliveryDate !== 'TBD' ? `<div class="row"><span class="label">Delivery Date</span><span class="value">${deliveryDate}</span></div>` : ''}

  <div class="section-title">Payment</div>
  <div class="row"><span class="label">Payment Method</span><span class="value">Card ending ••••</span></div>
  <div class="row"><span class="label">Payment Date</span><span class="value">${invoiceDate}</span></div>
  <div class="row"><span class="label">Billed To</span><span class="value">${user?.email || '—'}</span></div>
  <div class="row"><span class="label">Status</span><span class="value" style="color:#16a34a;">Paid</span></div>

  <div class="total-row"><span>Total Charged</span><span class="value">${totalDisplay}${order.term === 'three' ? '/mo' : ''}</span></div>

  <div class="footer">
    ZipAdBook · noreply@nirajprojects.shop · zipadbook.com<br/>
    This document serves as your official payment receipt. Please retain for your accounting records.
  </div>
</body>
</html>`;

      const win = window.open('', '_blank');
      if (win) {
        win.document.write(html);
        win.document.close();
        win.focus();
        setTimeout(() => { win.print(); }, 500);
      }
    };

    return (
      <div className="animate-slide-up space-y-5">
        {/* Success header */}
        <div className="text-center py-4">
          <div className="relative inline-flex mb-4">
            <div className="w-20 h-20 rounded-full bg-green-500/15 border border-green-500/30 flex items-center justify-center">
              <CheckCircle size={36} className="text-green-400" />
            </div>
            <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-brand-500 border-2 border-[hsl(var(--background))] flex items-center justify-center">
              <span className="text-white text-xs font-bold">✓</span>
            </span>
          </div>
          <h2 className="font-display text-2xl font-700 text-white mb-1">Payment Confirmed!</h2>
          <p className="text-slate-400 text-sm">Your ad placement is reserved and your campaign is locked in.</p>
        </div>

        {/* Booking Reference */}
        <div className="glass-card rounded-2xl p-4 border border-brand-600/30 bg-brand-600/5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-brand-600/20 border border-brand-600/30 flex items-center justify-center shrink-0">
              <FileText size={16} className="text-brand-400" />
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Booking Reference</p>
              <p className="font-mono text-lg font-bold text-brand-300">{bookingRef}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/10 border border-green-500/25">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-xs font-semibold text-green-400">Paid</span>
            </div>
          </div>
        </div>

        {/* Order Summary */}
        <div className="glass-card rounded-2xl border border-white/10 overflow-hidden">
          <div className="px-4 py-3 border-b border-white/8 flex items-center gap-2">
            <Receipt size={14} className="text-brand-400" />
            <span className="text-sm font-semibold text-white">Order Summary</span>
          </div>
          <div className="px-4 py-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Issue</span>
              <span className="text-white font-medium">{order.issueLabel || order.issue || '—'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Placement</span>
              <span className="text-white font-medium capitalize">{order.placement}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Regions</span>
              <span className="text-white font-medium">{order.regions.length} region{order.regions.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Term</span>
              <span className="text-white font-medium">{order.term === 'three' ? '3-Issue Subscription' : '1 Issue'}</span>
            </div>
            {artworkDeadline && artworkDeadline !== 'TBD' && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Artwork Deadline</span>
                <span className="text-amber-300 font-semibold">{artworkDeadline}</span>
              </div>
            )}
            {deliveryDate && deliveryDate !== 'TBD' && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Delivery Date</span>
                <span className="text-green-400 font-medium">{deliveryDate}</span>
              </div>
            )}
          </div>
        </div>

        {/* Payment Receipt */}
        <div className="glass-card rounded-2xl border border-white/10 overflow-hidden">
          <div className="px-4 py-3 border-b border-white/8 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <CreditCard size={14} className="text-brand-400" />
              <span className="text-sm font-semibold text-white">Payment Receipt</span>
            </div>
            <button
              type="button"
              onClick={handleDownloadInvoice}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs font-semibold text-slate-300 hover:bg-white/10 hover:text-white transition-colors"
            >
              <Download size={12} />
              Save PDF
            </button>
          </div>
          <div className="px-4 py-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Payment method</span>
              <span className="text-slate-300">Card ending ••••</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Date</span>
              <span className="text-white font-medium">{new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            </div>
            {paymentIntentId && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Transaction ID</span>
                <span className="font-mono text-xs text-slate-400 truncate max-w-[160px]">{paymentIntentId}</span>
              </div>
            )}
            <div className="border-t border-white/8 pt-3 flex justify-between items-center">
              <span className="font-semibold text-white text-sm">Total Charged</span>
              <span className="font-display font-bold text-xl text-brand-400 tabular-nums">
                ${verifiedTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                {order.term === 'three' && <span className="text-sm font-normal text-slate-400">/mo</span>}
              </span>
            </div>
          </div>
          <div className="px-4 py-3 border-t border-white/8 bg-white/2">
            <div className="flex items-center gap-2">
              <Mail size={12} className="text-brand-400 shrink-0" />
              <p className="text-xs text-slate-400">
                Full receipt sent to <span className="text-brand-300 font-medium">{user?.email || 'your email'}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Next Steps Timeline */}
        <div className="glass-card rounded-2xl border border-white/10 overflow-hidden">
          <div className="px-4 py-3 border-b border-white/8">
            <p className="text-sm font-semibold text-white">What Happens Next</p>
          </div>
          <div className="divide-y divide-white/8">
            <div className="px-4 py-4 flex items-start gap-3">
              <div className="flex flex-col items-center shrink-0">
                <div className="w-8 h-8 rounded-full bg-green-500/15 border border-green-500/30 flex items-center justify-center">
                  <CheckCircle size={14} className="text-green-400" />
                </div>
                <div className="w-px h-full bg-white/8 mt-1 flex-1 min-h-[16px]" />
              </div>
              <div className="pb-1">
                <p className="text-sm font-semibold text-white mb-0.5">Payment confirmed</p>
                <p className="text-xs text-slate-400">Your placement is reserved. Confirmation email sent to your inbox.</p>
              </div>
            </div>
            {needsArtwork && (
              <div className="px-4 py-4 flex items-start gap-3">
                <div className="flex flex-col items-center shrink-0">
                  <div className="w-8 h-8 rounded-full bg-amber-500/15 border border-amber-500/25 flex items-center justify-center">
                    <FileText size={14} className="text-amber-400" />
                  </div>
                  <div className="w-px h-full bg-white/8 mt-1 flex-1 min-h-[16px]" />
                </div>
                <div className="pb-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-sm font-semibold text-white">Upload your artwork</p>
                    {artworkDeadline && artworkDeadline !== 'TBD' && (
                      <span className="text-xs font-mono text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded-full">
                        Due {artworkDeadline}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400">Submit your print-ready ad via My Orders. Our team reviews within 1 business day.</p>
                </div>
              </div>
            )}
            {isDesignService && (
              <div className="px-4 py-4 flex items-start gap-3">
                <div className="flex flex-col items-center shrink-0">
                  <div className="w-8 h-8 rounded-full bg-purple-500/15 border border-purple-500/25 flex items-center justify-center">
                    <Palette size={14} className="text-purple-400" />
                  </div>
                  <div className="w-px h-full bg-white/8 mt-1 flex-1 min-h-[16px]" />
                </div>
                <div className="pb-1">
                  <p className="text-sm font-semibold text-white mb-0.5">Design team assigned</p>
                  <p className="text-xs text-slate-400">Our design team will reach out within 1–2 business days to begin your ad creative.</p>
                </div>
              </div>
            )}
            <div className="px-4 py-4 flex items-start gap-3">
              <div className="flex flex-col items-center shrink-0">
                <div className="w-8 h-8 rounded-full bg-brand-600/20 border border-brand-600/30 flex items-center justify-center">
                  <Clock size={14} className="text-brand-400" />
                </div>
                <div className="w-px h-full bg-white/8 mt-1 flex-1 min-h-[16px]" />
              </div>
              <div className="pb-1">
                <p className="text-sm font-semibold text-white mb-0.5">Print &amp; production</p>
                <p className="text-xs text-slate-400">Your ad goes to print after artwork approval. We'll keep you updated on progress.</p>
              </div>
            </div>
            <div className="px-4 py-4 flex items-start gap-3">
              <div className="shrink-0">
                <div className="w-8 h-8 rounded-full bg-green-500/10 border border-green-500/20 flex items-center justify-center">
                  <CheckCircle size={14} className="text-green-400" />
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold text-white mb-0.5">Campaign goes live</p>
                <p className="text-xs text-slate-400">
                  Your ZipAdBook is distributed across your selected regions
                  {deliveryDate && deliveryDate !== 'TBD' ? ` starting ${deliveryDate}` : ' on the scheduled delivery date'}.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Customer Hub Inbox link */}
        <div className="glass-card rounded-2xl border border-brand-600/25 bg-brand-600/5 p-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-brand-600/20 border border-brand-600/30 flex items-center justify-center shrink-0">
            <Inbox size={18} className="text-brand-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white mb-0.5">Questions about your order?</p>
            <p className="text-xs text-slate-400">Message our team directly from your Customer Hub Inbox.</p>
          </div>
          <a
            href="/customer-hub?tab=inbox"
            className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl bg-brand-600/20 border border-brand-600/30 text-xs font-semibold text-brand-300 hover:bg-brand-600/35 transition-colors"
          >
            Open Inbox
            <ArrowRight size={12} />
          </a>
        </div>

        {/* CTA button */}
        <button
          type="button"
          onClick={handleGoToOrderSuccess}
          className="w-full flex items-center justify-center gap-2 py-4 text-base font-semibold btn-primary"
          style={{ minHeight: '56px' }}
        >
          View Full Order Confirmation
          <ArrowRight size={16} />
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <Lock size={14} className="text-green-400" />
        <span className="text-xs text-slate-400">Secured by Stripe · 256-bit SSL encryption</span>
        <div className="ml-auto flex items-center gap-1.5">
          {['VISA', 'MC', 'AMEX'].map((brand) => (
            <span key={`card-brand-${brand}`} className="px-2 py-0.5 bg-white/8 rounded text-xs text-slate-400 font-mono">{brand}</span>
          ))}
        </div>
      </div>

      {/* Stripe Payment Element */}
      <div className="p-4 bg-navy-700 border border-white/12 rounded-xl">
        <PaymentElement
          options={{
            layout: 'tabs',
          }}
        />
      </div>

      {/* SMS Consent */}
      <label className="flex items-start gap-3 cursor-pointer group">
        <input
          type="checkbox"
          checked={smsConsent}
          onChange={e => setSmsConsent(e.target.checked)}
          className="mt-0.5 w-4 h-4 rounded border-white/20 bg-navy-700 accent-brand-500 shrink-0"
        />
        <span className="text-xs text-slate-400 leading-relaxed">
          By checking this box, you agree to receive SMS messages from ZipAdBBook by Take A Zip LLC related to your Placements, Ads or Flyer distribution services inquiry and follow-ups. Message &amp; data rates may apply. Message frequency varies. Reply STOP to unsubscribe, HELP for help. Take A Zip LLC.
        </span>
      </label>

      <button
        type="submit"
        disabled={processing || !stripe || !elements}
        className="w-full flex items-center justify-center gap-2 py-4 text-base font-semibold btn-primary disabled:opacity-70"
        style={{ minHeight: '56px' }}
      >
        {processing ? (
          <>
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Processing payment...
          </>
        ) : (
          <>
            <Lock size={16} />
            {order.term === 'three'
              ? `Start Subscription — $${verifiedTotal.toLocaleString()}/mo`
              : `Pay $${verifiedTotal.toLocaleString()} — Confirm Booking`}
          </>
        )}
      </button>
    </form>
  );
}

// ─── Exchange confirmation (no payment needed) ────────────────────────────────
interface ExchangeFormProps {
  order: OrderState;
  orderId: string;
  orderNumber: string;
  coupon: AppliedCoupon;
  onComplete: () => void;
}

function ExchangeForm({ order, orderId, orderNumber, coupon, onComplete }: ExchangeFormProps) {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const router = useRouter();
  const { user } = useAuth();

  const handleSubmit = async () => {
    setSubmitting(true);
    const supabase = createClient();
    // Record coupon usage atomically via server-side RPC
    await supabase.rpc('redeem_coupon' as any, {
      p_coupon_id: coupon.id,
      p_user_id: user?.id ?? null,
      p_order_id: orderId || null,
      p_business_email: user?.email ?? null,
      p_exchange_status: 'pending',
    }).catch(() => null);
    // Update order exchange status
    if (orderId) {
      await supabase.from('orders').update({
        coupon_id: coupon.id,
        coupon_code: coupon.code,
        coupon_discount_amount: 0,
        exchange_status: 'pending',
        payment_status: 'pending',
      }).eq('id', orderId);

      // ── Create artwork intake request so designer workflow is triggered ──
      try {
        let clientBrand = user?.user_metadata?.full_name || 'Unknown';
        const { data: profile } = await supabase.from('user_profiles').select('business_name, full_name').eq('id', user?.id ?? '').maybeSingle();
        if (profile) clientBrand = profile.business_name || profile.full_name || clientBrand;

        const isDesignService = order.artworkType === 'design-service' || order.addDesign;
        const deadlineDate = order.issueArtworkDeadline
          ? new Date(order.issueArtworkDeadline).toISOString().split('T')[0]
          : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

        // Check if intake already exists
        const { data: existingIntake } = await supabase
          .from('artwork_intake_requests')
          .select('id')
          .eq('order_id', orderId)
          .maybeSingle();

        if (!existingIntake) {
          const { data: intakeRecord } = await supabase.from('artwork_intake_requests').insert({
            project_name: `${orderNumber} — ${order.issueLabel || order.issue || 'Issue'}`,
            campaign_name: orderNumber,
            client_brand: clientBrand,
            region_ids: order.regions,
            placement_location: order.placement,
            artwork_type: isDesignService ? 'design_service' : 'client_upload',
            deadline: deadlineDate,
            priority: 'medium',
            text_content: order.designBrief || null,
            special_instructions: isDesignService
              ? `Design service order (exchange/barter coupon). Brief: ${order.designBrief || 'No brief provided.'}`
              : 'Client will upload artwork (exchange/barter deal). Please review when submitted.',
            status: 'not_started',
            order_id: orderId,
            submitted_by: user?.id ?? null,
          }).select('id').single();

          // Auto-assign designer
          if (intakeRecord?.id) {
            const { data: assignResult } = await supabase.rpc('perform_auto_assignment' as any, { p_intake_id: intakeRecord.id }).catch(() => ({ data: null }));
            // If auto-assignment succeeded, update order's artwork_assigned_to
            if (assignResult && (assignResult as any).status === 'assigned' && (assignResult as any).designer_id) {
              await supabase.from('orders').update({
                artwork_assigned_to: (assignResult as any).designer_id,
              }).eq('id', orderId);
            }
          }
        }
      } catch { /* non-fatal */ }
    }
    setDone(true);
    setSubmitting(false);
    onComplete();
    const params = new URLSearchParams({ orderId: orderNumber, placement: order.placement, regions: order.regions.join(','), total: '0', term: order.term });
    if (order.issueLabel) params.set('issueLabel', order.issueLabel);
    router.push(`/order-success?${params.toString()}`);
  };

  if (done) {
    return (
      <div className="text-center py-8">
        <CheckCircle size={36} className="text-green-400 mx-auto mb-3" />
        <p className="text-white font-semibold">Barter deal submitted!</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/25">
        <p className="text-sm font-semibold text-amber-400 mb-1">Exchange / Barter Deal</p>
        <p className="text-xs text-slate-400">
          Your coupon <strong className="text-white font-mono">{coupon.code}</strong> covers this placement in exchange for goods or services. No payment is required now. Your order will be marked as <strong className="text-amber-400">Pending Barter Approval</strong> and an admin will review and confirm the deal.
        </p>
      </div>
      <button
        type="button"
        onClick={handleSubmit}
        disabled={submitting}
        className="w-full flex items-center justify-center gap-2 py-4 text-base font-semibold btn-primary disabled:opacity-70"
        style={{ minHeight: '56px' }}
      >
        {submitting ? <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />Submitting...</> : 'Submit Barter Deal Request'}
      </button>
    </div>
  );
}

// ─── Outer StepG — creates payment intent, then renders Elements ─────────────
export default function StepG({ order, updateOrder, onComplete }: Props) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [orderId, setOrderId] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [verifiedTotal, setVerifiedTotal] = useState(0);
  // Derive artwork deadline and delivery date from the selected issue in order state
  const artworkDeadline = order.issueArtworkDeadline || 'TBD';
  const deliveryDate = order.issuePrintDate || 'TBD';
  const [intentError, setIntentError] = useState<string | null>(null);
  const [intentLoading, setIntentLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const { user } = useAuth();

  // ── Membership discount ────────────────────────────────────────────────────
  const [userMembership, setUserMembership] = useState<UserMembership | null>(null);

  useEffect(() => {
    if (!user?.id) return;
    getUserMembership(user.id).then(setUserMembership).catch(() => null);
  }, [user?.id]);

  // Load live pricing from DB (falls back to hardcoded if DB unavailable)
  const { placements: catalogPlacements, loading: catalogLoading } = usePlacementCatalog(order.issue || undefined);

  // Build live pricing map from DB catalog
  const livePrices: Record<string, { one: number; three: number }> = {};
  catalogPlacements.forEach((p) => {
    if (p.id && p.oneIssue !== undefined && p.threeIssue !== undefined) {
      livePrices[p.id] = { one: p.oneIssue, three: p.threeIssue };
    }
  });
  // Merge with hardcoded fallback (DB prices take priority)
  const placementPricesLive: Record<string, { one: number; three: number }> = {
    ...placementPrices,
    ...livePrices,
  };

  // Coupon state
  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(null);
  const [couponFinalTotal, setCouponFinalTotal] = useState<number | null>(null);

  // Compute display totals for the summary panel
  const prices = placementPricesLive[order.placement] || placementPricesLive.standard;
  const baseRate = order.term === 'one' ? prices.one : prices.three;
  const getDiscount = (count: number) => {
    if (order.placement !== 'standard') return 0;
    if (count >= 8) return 0.15;
    if (count >= 5) return 0.10;
    if (count >= 3) return 0.05;
    return 0;
  };
  const discount = getDiscount(order.regions.length);
  const discountedRate = Math.round(baseRate * (1 - discount));
  const subtotal = discountedRate * order.regions.length;
  const designFee = order.addDesign ? 150 : 0;
  const subtotalWithDesign = subtotal + designFee;

  // Apply membership discount after multi-region discount
  const membershipDiscount = userMembership
    ? calculateAdDiscount(userMembership, subtotalWithDesign)
    : { discountRate: 0, discountAmount: 0, finalPrice: subtotalWithDesign, discountLabel: '' };

  const displayTotal = membershipDiscount.finalPrice;

  // Effective total after coupon
  const effectiveTotal = couponFinalTotal !== null ? couponFinalTotal : displayTotal;
  const couponSavings = displayTotal - effectiveTotal;

  const handleCouponApplied = (coupon: AppliedCoupon | null, finalTotal: number) => {
    setAppliedCoupon(coupon);
    setCouponFinalTotal(coupon ? finalTotal : null);
    // Propagate coupon info to OrderState so OrderSummaryPanel reflects the correct total
    updateOrder({
      couponCode: coupon ? coupon.code : null,
      couponDiscount: coupon ? displayTotal - finalTotal : 0,
      couponFinalTotal: coupon ? finalTotal : null,
    });
  };

  useEffect(() => {
    // Skip payment intent for FREE/EXCHANGE coupons
    if (appliedCoupon && (appliedCoupon.coupon_type === 'FREE_PLACEMENT' || appliedCoupon.coupon_type === 'EXCHANGE')) {
      setIntentLoading(false);
      return;
    }

    const createIntent = async () => {
      setIntentLoading(true);
      setIntentError(null);
      setClientSecret(null);
      try {
        const res = await fetch('/api/stripe/create-payment-intent', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: user?.id ?? null,
            email: user?.email ?? '',
            cardholderName: user?.user_metadata?.full_name ?? '',
            placement: order.placement,
            regions: order.regions,
            term: order.term,
            total: effectiveTotal,
            addDesign: order.addDesign,
            issueLabel: order.issueLabel || order.issue || 'Unknown Issue',
            issueId: order.issue,
            artworkDeadline,
            deliveryDate,
            couponCode: appliedCoupon?.code ?? null,
            designBrief: order.designBrief || null,
            artworkType: order.artworkType || null,
          }),
        });

        const data = await res.json();

        if (!res.ok) {
          const msg = data?.error ?? 'Payment setup failed';
          setIntentError(msg);
          return;
        }

        setClientSecret(data.clientSecret);
        setOrderId(data.orderId || '');
        setOrderNumber(data.orderNumber);
        setPaymentIntentId(data.paymentIntentId);
        setVerifiedTotal(data.verifiedTotal);

        if (data.orderInsertError) {
          console.warn('[StepG] Order record insert failed:', data.orderInsertError, '— payment intent still valid');
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Payment setup failed';
        setIntentError(msg);
      } finally {
        setIntentLoading(false);
      }
    };

    createIntent();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedCoupon, retryCount]);

  const stripePromise = getStripe();

  const elementsOptions: StripeElementsOptions = clientSecret
    ? {
        clientSecret,
        appearance: {
          theme: 'night',
          variables: {
            colorPrimary: '#6366f1',
            colorBackground: '#1e293b',
            colorText: '#e2e8f0',
            colorDanger: '#f87171',
            fontFamily: 'system-ui, sans-serif',
            borderRadius: '12px',
          },
        },
      }
    : {};

  const isFreeOrExchange = appliedCoupon && (appliedCoupon.coupon_type === 'FREE_PLACEMENT' || appliedCoupon.coupon_type === 'EXCHANGE');

  return (
    <div className="animate-slide-up">
      <div className="mb-6">
        <h2 className="font-display text-2xl font-700 text-white mb-1">Payment</h2>
        <p className="text-sm text-slate-400">
          {order.term === 'three' ?'Your card will be charged monthly for 3 consecutive issues. Cancel before issue 2 to stop future billing.' :'One-time charge. Your placement is confirmed immediately upon payment.'}
        </p>
      </div>

      {/* Order total recap */}
      <div className="glass-card rounded-2xl p-5 border border-brand-600/20 mb-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-white">Order Total</p>
          {order.term === 'three' && <span className="chip bg-brand-600/20 border-brand-600/30 text-brand-300 text-xs">Monthly subscription</span>}
        </div>
        <div className="space-y-2">
          {discount > 0 ? (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">{placementNames[order.placement]} × {order.regions.length} region{order.regions.length > 1 ? 's' : ''}</span>
                <span className="text-slate-500 tabular-nums line-through">${(baseRate * order.regions.length).toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-green-400">Multi-region discount ({(discount * 100).toFixed(0)}%)</span>
                <span className="text-green-400 tabular-nums">-${Math.round(baseRate * discount * order.regions.length).toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Subtotal</span>
                <span className="text-white tabular-nums">${subtotal.toLocaleString()}</span>
              </div>
            </>
          ) : (
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">{placementNames[order.placement]} × {order.regions.length} region{order.regions.length > 1 ? 's' : ''}</span>
              <span className="text-white tabular-nums">${subtotal.toLocaleString()}</span>
            </div>
          )}
          {designFee > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Design Service</span>
              <span className="text-white tabular-nums">${designFee}</span>
            </div>
          )}
          {membershipDiscount.discountAmount > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-amber-400 flex items-center gap-1.5">
                <Crown size={12} />
                {membershipDiscount.discountLabel}
              </span>
              <span className="text-amber-400 tabular-nums">-${membershipDiscount.discountAmount.toLocaleString()}</span>
            </div>
          )}
          {couponSavings > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-green-400 flex items-center gap-1.5"><Tag size={12} />Coupon ({appliedCoupon?.code})</span>
              <span className="text-green-400 tabular-nums">-${couponSavings.toLocaleString()}</span>
            </div>
          )}
          <div className="border-t border-white/10 pt-2 flex justify-between">
            <span className="font-semibold text-white">Total{order.term === 'three' ? ' / month' : ''}</span>
            <span className="font-display font-bold text-xl text-brand-400 tabular-nums">${effectiveTotal.toLocaleString()}</span>
          </div>
          {order.term === 'three' && (
            <p className="text-xs text-slate-500 mt-1">Billed monthly × 3 issues = ${(effectiveTotal * 3).toLocaleString()} total</p>
          )}
        </div>
      </div>

      {/* Coupon apply widget */}
      <div className="mb-5">
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
          <Tag size={12} />
          Have a coupon code?
        </p>
        <CouponWidget
          userId={user?.id || ''}
          baseTotal={displayTotal}
          onCouponApplied={handleCouponApplied}
        />
      </div>

      {/* Exchange flow */}
      {appliedCoupon?.coupon_type === 'EXCHANGE' ? (
        <ExchangeForm
          order={order}
          orderId={orderId}
          orderNumber={orderNumber}
          coupon={appliedCoupon}
          onComplete={onComplete}
        />
      ) : appliedCoupon?.coupon_type === 'FREE_PLACEMENT' ? (
        /* Free placement — skip Stripe entirely */
        <FreePlacementForm
          order={order}
          coupon={appliedCoupon}
          onComplete={onComplete}
          updateOrder={updateOrder}
        />
      ) : intentLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-brand-500/30 border-t-brand-400 rounded-full animate-spin" />
          <span className="ml-3 text-sm text-slate-400">Setting up secure payment...</span>
        </div>
      ) : intentError ? (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/25">
          <AlertTriangle size={16} className="text-red-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-400">Payment setup failed. Please try again.</p>
            <p className="text-xs text-slate-400 mt-1">{intentError}</p>
            <button onClick={() => setRetryCount(c => c + 1)} className="mt-2 text-xs text-brand-400 hover:text-brand-300 underline">Try again</button>
          </div>
        </div>
      ) : clientSecret ? (
        <Elements stripe={stripePromise} options={elementsOptions}>
          <PaymentForm
            order={order}
            updateOrder={updateOrder}
            onComplete={onComplete}
            clientSecret={clientSecret}
            orderId={orderId}
            orderNumber={orderNumber}
            paymentIntentId={paymentIntentId}
            verifiedTotal={verifiedTotal}
            artworkDeadline={artworkDeadline}
            deliveryDate={deliveryDate}
          />
        </Elements>
      ) : null}
    </div>
  );
}

// ─── Free Placement Form ──────────────────────────────────────────────────────
interface FreePlacementFormProps {
  order: OrderState;
  coupon: AppliedCoupon;
  onComplete: () => void;
  updateOrder: (updates: Partial<OrderState>) => void;
}

function FreePlacementForm({ order, coupon, onComplete, updateOrder }: FreePlacementFormProps) {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const router = useRouter();
  const { user } = useAuth();

  const handleSubmit = async () => {
    setSubmitting(true);
    const supabase = createClient();

    // Compute the base total so we can store the correct discount amount
    const { PLACEMENT_PRICES } = await import('@/lib/pricingConfig');
    const prices = PLACEMENT_PRICES[order.placement] ?? PLACEMENT_PRICES.standard;
    const baseRate = order.term === 'one' ? prices.one : prices.three;
    const count = order.regions.length;
    let disc = 0;
    if (order.placement === 'standard') {
      if (count >= 8) disc = 0.15;
      else if (count >= 5) disc = 0.10;
      else if (count >= 3) disc = 0.05;
    }
    const subtotal = Math.round(baseRate * (1 - disc)) * count;
    const baseTotal = subtotal + (order.addDesign ? 150 : 0);

    const orderNumber = `ZAB-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 9000) + 1000)}`;

    const isDesignService = order.artworkType === 'design-service' || order.addDesign;

    let orderData: { id: string; order_number: string } | null = null;

    try {
      // Create order record with $0 total and correct discount amount
      const { data: insertedOrder, error: insertError } = await supabase.from('orders').insert({
        user_id: user?.id ?? null,
        order_number: orderNumber,
        placement: order.placement,
        regions: order.regions,
        term: order.term,
        total: 0,
        coupon_id: coupon.id,
        coupon_code: coupon.code,
        coupon_discount_amount: baseTotal,
        payment_status: 'paid',
        order_status: 'confirmed',
        issue_id: order.issue || null,
        issue_label: order.issueLabel || null,
        artwork_deadline: order.issueArtworkDeadline || null,
        print_date: order.issuePrintDate || null,
        add_design: order.addDesign,
        payment_method: 'coupon',
        artwork_status: isDesignService ? 'design_service_requested' : 'awaiting_upload',
        artwork_type: order.artworkType || (order.addDesign ? 'design-service' : 'upload'),
        design_brief: order.designBrief || null,
      }).select('id, order_number').single();

      if (insertError) {
        console.error('[FreePlacementForm] Order insert error:', insertError.message);
      } else {
        orderData = insertedOrder;
      }

      if (orderData) {
        // ── Upload artwork file if provided ──────────────────────────────────
        if (order.artworkFile) {
          try {
            const userId = user?.id ?? 'anon';
            const ext = order.artworkFile.name.split('.').pop() || 'bin';
            const filePath = `${userId}/${orderData.id}.${ext}`;
            const { data: uploadData, error: uploadError } = await supabase.storage
              .from('artwork-uploads')
              .upload(filePath, order.artworkFile, { contentType: order.artworkFile.type, upsert: true });
            if (!uploadError) {
              const { data: urlData } = supabase.storage.from('artwork-uploads').getPublicUrl(filePath);
              const artworkUrl = urlData?.publicUrl || filePath;
              await supabase.from('orders').update({ artwork_review_url: artworkUrl, artwork_status: 'uploaded' }).eq('id', orderData.id);
              await supabase.from('artwork_versions').insert({
                order_id: orderData.id,
                version_number: 1,
                file_url: artworkUrl,
                file_name: order.artworkFile.name,
                file_size_bytes: order.artworkFile.size,
                submitted_by: user?.id ?? null,
                status: 'pending_review',
              }).catch(() => null);

              // Trigger artwork_received email notification
              const baseUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://zipadbook.com';
              fetch(`${baseUrl}/api/orders/send-status-email`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ orderId: orderData.id, eventType: 'artwork_received' }),
              }).catch(() => null);
            }
          } catch { /* non-fatal */ }
        }

        // ── Create artwork intake request (designer assignment workflow) ──────
        try {
          let clientBrand = user?.user_metadata?.full_name || 'Unknown';
          const { data: profile } = await supabase.from('user_profiles').select('business_name, full_name').eq('id', user?.id ?? '').maybeSingle();
          if (profile) clientBrand = profile.business_name || profile.full_name || clientBrand;

          const deadlineDate = order.issueArtworkDeadline
            ? new Date(order.issueArtworkDeadline).toISOString().split('T')[0]
            : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

          const { data: intakeRecord } = await supabase.from('artwork_intake_requests').insert({
            project_name: `${orderData.order_number} — ${order.issueLabel || order.issue || 'Issue'}`,
            campaign_name: orderData.order_number,
            client_brand: clientBrand,
            region_ids: order.regions,
            placement_location: order.placement,
            artwork_type: isDesignService ? 'design_service' : 'client_upload',
            deadline: deadlineDate,
            priority: 'medium',
            text_content: order.designBrief || null,
            special_instructions: isDesignService
              ? `Design service order (free placement coupon). Brief: ${order.designBrief || 'No brief provided.'}`
              : 'Client will upload artwork. Please review when submitted.',
            status: 'not_started',
            order_id: orderData.id,
            submitted_by: user?.id ?? null,
          }).select('id').single();

          if (intakeRecord?.id) {
            const { data: assignResult } = await supabase.rpc('perform_auto_assignment' as any, { p_intake_id: intakeRecord.id }).catch(() => ({ data: null }));
            // If auto-assignment succeeded, update order's artwork_assigned_to
            if (assignResult && (assignResult as any).status === 'assigned' && (assignResult as any).designer_id) {
              await supabase.from('orders').update({
                artwork_assigned_to: (assignResult as any).designer_id,
              }).eq('id', orderData.id);
            }
          }
        } catch { /* non-fatal */ }

        // Record coupon usage atomically via server-side RPC
        await supabase.rpc('redeem_coupon' as any, {
          p_coupon_id: coupon.id,
          p_user_id: user?.id ?? null,
          p_order_id: orderData.id,
          p_business_email: user?.email ?? null,
          p_exchange_status: null,
        }).catch(() => null);

        // Send confirmation emails (non-blocking)
        const customerEmail = user?.email || '';
        const customerName = user?.user_metadata?.full_name || '';
        if (customerEmail) {
          try {
            const { sendOrderConfirmationEmail, sendAdminNotificationEmail } = await import('@/lib/email/emailService');
            sendOrderConfirmationEmail({
              customerEmail,
              customerName,
              orderId: orderData.order_number || orderData.id,
              orderNumber: orderData.order_number,
              placement: order.placement,
              regions: order.regions,
              total: 0,
              term: order.term,
              artworkDeadline: order.issueArtworkDeadline || 'TBD',
              deliveryDate: order.issuePrintDate || 'TBD',
              issueLabel: order.issueLabel,
              couponCode: coupon.code,
              couponDiscount: baseTotal,
              subtotal: baseTotal,
              paymentMethod: 'coupon',
            } as any).catch(console.error);

            sendAdminNotificationEmail({
              customerEmail,
              customerName,
              orderId: orderData.order_number || orderData.id,
              orderNumber: orderData.order_number,
              placement: order.placement,
              regions: order.regions,
              total: 0,
              term: order.term,
              artworkDeadline: order.issueArtworkDeadline || 'TBD',
              deliveryDate: order.issuePrintDate || 'TBD',
            } as any).catch(console.error);
          } catch (emailErr) {
            console.error('[FreePlacementForm] Email send error:', emailErr);
            // Non-fatal — continue with redirect
          }
        }
      }
    } catch (err) {
      console.error('[FreePlacementForm] Unexpected error during order creation:', err);
      // Non-fatal — still redirect so user sees confirmation
    } finally {
      setSubmitting(false);
    }

    setDone(true);
    updateOrder({ paymentComplete: true });
    onComplete();

    const finalOrderNumber = orderData?.order_number || orderNumber;
    const params = new URLSearchParams({
      orderId: finalOrderNumber,
      placement: order.placement,
      regions: order.regions.join(','),
      total: '0',
      term: order.term,
    });
    if (order.issueLabel) params.set('issueLabel', order.issueLabel);
    router.push(`/order-success?${params.toString()}`);
  };

  if (done) {
    return (
      <div className="text-center py-8">
        <CheckCircle size={36} className="text-green-400 mx-auto mb-3" />
        <p className="text-white font-semibold">Free placement confirmed!</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/25">
        <p className="text-sm font-semibold text-green-400 mb-1">Free Placement Coupon Applied</p>
        <p className="text-xs text-slate-400">
          Coupon <strong className="text-white font-mono">{coupon.code}</strong> covers this placement at no cost. Click below to confirm your booking.
        </p>
      </div>
      <button
        type="button"
        onClick={handleSubmit}
        disabled={submitting}
        className="w-full flex items-center justify-center gap-2 py-4 text-base font-semibold btn-primary disabled:opacity-70"
        style={{ minHeight: '56px' }}
      >
        {submitting ? <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />Confirming...</> : <><CheckCircle size={16} />Confirm Free Placement</>}
      </button>
    </div>
  );
}